// React and hooks
import React, { useEffect, useMemo, useState } from 'react'

// Relative imports
import { AppointmentFormData } from './Appointment.modal'
import { SHOW_APPOINTMENT_LAB_ORDERS } from './Appointment.constant'
import documentIcon from '@assets/images/document_2_fill.svg'
import { Button } from '@components/common'
import { Col, Form, Row, Table } from 'react-bootstrap'
import { groupProceduresByCode } from './Plan.component'
import { IProcedureLogs } from '@dataTypes/appointment.type'
import { FormTemplateStatus } from '@dataTypes/forms.type'
import moment from 'moment'
import { getFormTemplates } from '@services/api/forms.api'
import { useSelector } from 'react-redux'
import { RootState } from '@store/index'
import { useAppSelector } from '@store/hooks'
import { INote, NoteContextType } from '@dataTypes/note.type'
import type { IFavoritedUser } from '@dataTypes/user-favorite.type'
import { v4 as uuid } from 'uuid'
import DataTreeGridComponent from '@components/common/DataTreeGridComponent/DataTreeGridComponent'
import type { AppointmentNoteOverlayInput } from '@pages/Appointment/appointmentNoteOverlay.util'
import {
  formatNoteTitleWithVersion,
  type NoteVersionChainMember,
} from '@utils/noteVersion.util'
import { formatNoteUpdaterInitialLast } from '@utils/noteSidebarAuthor.util'
import { formatToothSurfaceEntriesForDisplay } from '@utils/procedure-surface-selection.utils'
import { htmlToPlainTextPreview } from '@utils/htmlPlainText.util'

const normalizeUserId = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : ''

const sessionFirstLastNames = (
  sessionUser: Record<string, unknown>,
): { first: string | null; last: string | null } => {
  const rawFirst =
    (typeof sessionUser.first_name === 'string' && sessionUser.first_name) ||
    (typeof sessionUser.firstName === 'string' && sessionUser.firstName) ||
    ''
  const rawLast =
    (typeof sessionUser.last_name === 'string' && sessionUser.last_name) ||
    (typeof sessionUser.lastName === 'string' && sessionUser.lastName) ||
    ''
  const first = rawFirst.trim() !== '' ? rawFirst.trim() : null
  const last = rawLast.trim() !== '' ? rawLast.trim() : null
  return { first, last }
}

/**
 * Match note side panel naming (`formatNoteUpdaterInitialLast` on `updatedBy`).
 * Form-state notes usually have `updated_by` but no `updatedBy`; if that id is the current user
 * (or missing), show first/last from localStorage, then `chatInternal.loggedInUser` — not a
 * UUID fragment (unlike the list footer fallback).
 */
const getReviewNoteAuthorLabel = (
  note: Partial<INote>,
  sessionUser: Record<string, unknown> & { user_id?: string },
  loggedInUser: IFavoritedUser | null,
): string => {
  const fromJoined = formatNoteUpdaterInitialLast(
    note.updatedBy?.first_name,
    note.updatedBy?.last_name,
  )
  if (fromJoined.length > 0) {
    return fromJoined
  }

  const sessionId = normalizeUserId(sessionUser.user_id)
  const reduxUserId = normalizeUserId(loggedInUser?.user_id ?? '')
  const effectiveOwnerId = sessionId || reduxUserId
  const updatedById = normalizeUserId(note.updated_by)
  const createdById = normalizeUserId(note.created_by)

  const { first, last } = sessionFirstLastNames(sessionUser)
  const fromLocalStorage = formatNoteUpdaterInitialLast(first, last)
  const fromLoggedInUser = loggedInUser
    ? formatNoteUpdaterInitialLast(
        loggedInUser.first_name,
        loggedInUser.last_name,
      )
    : ''
  const currentUserDisplayName =
    fromLocalStorage.length > 0 ? fromLocalStorage : fromLoggedInUser

  const matchesEffectiveOwner =
    updatedById === effectiveOwnerId || createdById === effectiveOwnerId
  const isCurrentUserNote =
    (effectiveOwnerId !== '' && matchesEffectiveOwner) ||
    (updatedById === '' && createdById === '')

  if (isCurrentUserNote) {
    return currentUserDisplayName
  }

  const foreignPrefix =
    (typeof note.updated_by === 'string' && note.updated_by.trim() !== ''
      ? note.updated_by.split('-')[0]
      : null) ??
    (typeof note.created_by === 'string' && note.created_by.trim() !== ''
      ? note.created_by.split('-')[0]
      : null) ??
    ''
  return foreignPrefix
}

type ReviewProps = {
  formData: Partial<AppointmentFormData>
  handleEditButton: () => void
  handleChange: (
    field: string,
    value: string | number | Date | boolean | string[] | Partial<INote>[],
  ) => void
  skippedSteps: { [key: number]: boolean }
  appointmentId?: string
  onViewNote?: (input: AppointmentNoteOverlayInput) => void
  mergeAppointmentSavedNote?: (note: INote) => void
}

const Review: React.FC<ReviewProps> = ({
  formData,
  handleEditButton,
  handleChange,
  skippedSteps,
  appointmentId,
  onViewNote,
  mergeAppointmentSavedNote,
}) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}') as Record<
    string,
    unknown
  > & { user_id?: string }
  const loggedInUser = useAppSelector(
    (state) => state.chatInternal.loggedInUser,
  )
  const operatoryList = useSelector((state: RootState) => state.operatory.data)
  const [notesList, setNotesList] = useState<Partial<INote>[]>([])
  const [formTemplateTitleById, setFormTemplateTitleById] = useState<
    Record<string, string>
  >({})
  const [formTitlesFetchPending, setFormTitlesFetchPending] = useState(false)

  useEffect(() => {
    const ids = formData.assignedFormIds ?? []
    if (ids.length === 0) {
      setFormTemplateTitleById({})
      setFormTitlesFetchPending(false)
      return
    }
    let cancelled = false
    setFormTitlesFetchPending(true)
    getFormTemplates({ status: FormTemplateStatus.Approved })
      .then((formTemplates) => {
        if (cancelled) return
        const next: Record<string, string> = {}
        for (const t of formTemplates) {
          if (t.form_template_id) {
            next[t.form_template_id] = (t.title ?? '').trim() || 'Untitled form'
          }
        }
        setFormTemplateTitleById((prev) => ({ ...prev, ...next }))
      })
      .finally(() => {
        if (!cancelled) setFormTitlesFetchPending(false)
      })
    return (): void => {
      cancelled = true
    }
  }, [formData.assignedFormIds])

  const schedDetails: { field: keyof AppointmentFormData; title: string }[] = [
    {
      field: 'patientName',
      title: 'Name',
    },
    {
      field: 'providerName',
      title: 'Provider',
    },
    {
      field: 'operatoryName',
      title: 'Operatory name',
    },
    {
      field: 'apptDate',
      title: 'Date',
    },
    {
      field: 'apptStartTime',
      title: 'Time',
    },
    {
      field: 'duration',
      title: 'Duration',
    },
    {
      field: 'appointmentTypeName',
      title: 'Appointment type',
    },
    {
      field: 'isRecurring',
      title: 'Recurring visit',
    },
    {
      field: 'isInsured',
      title: 'Is insured?',
    },
    // Correctly conditionally add the insurance field
    ...(formData.isInsured === 'Yes'
      ? [
          {
            field: 'insuranceName' as keyof AppointmentFormData,
            title: '',
          },
        ]
      : []),
  ]

  const displayText = (
    field: keyof AppointmentFormData,
  ): string | number | boolean | undefined => {
    if (field === 'isRecurring') {
      return formData.isRecurring ? 'Yes' : 'No'
    }

    if (field === 'isInsured') {
      return formData.isInsured ? 'Yes' : 'No'
    }

    if (field === 'duration') {
      return `${formData.duration} minutes`
    }

    if (field === 'operatoryName') {
      const name = operatoryList.find(
        (operatory) => operatory.operatory_id === formData.operatoryId,
      )

      return name?.name || ''
    }

    if (field === 'insuranceName') {
      const name = formData.insuranceOptions?.find(
        (insurance) => insurance.insurance_plan_id === formData.insuranceId,
      )

      return name?.plan_name || ''
    }

    if (
      field === 'procedures' ||
      field === 'contactVia' ||
      field === 'labOrders' ||
      field === 'insuranceOptions' ||
      field === 'tpId' ||
      field === 'notes' ||
      field === 'files' ||
      field === 'assignedFormIds'
    )
      return ''

    return formData[field]
  }

  const proceduresList = (formData.procedures ??
    []) as Partial<IProcedureLogs>[]

  const appointmentNoteVersionPool = useMemo((): NoteVersionChainMember[] => {
    const pid = formData.patientId ?? ''
    const ctxId = appointmentId ?? ''
    return (formData.notes ?? []).map((n) => ({
      title: n.title ?? '',
      version: n.version,
      is_latest: n.is_latest,
      note_group_id: n.note_group_id,
      patient_id: n.patient_id ?? pid,
      contact_id: n.contact_id ?? '',
      context_type: n.context_type ?? NoteContextType.APPOINTMENT,
      context_id: n.context_id ?? ctxId,
    }))
  }, [appointmentId, formData.notes, formData.patientId])

  const groupedProcedures = groupProceduresByCode(proceduresList)

  const toothSurfaceTemplate = ({
    entries,
  }: {
    entries: Partial<IProcedureLogs>[]
  }): JSX.Element => {
    return (
      <div className="template-cell-tooth-surface">
        {formatToothSurfaceEntriesForDisplay(
          entries.map((entry) => ({
            area_type: entry.area_type ?? null,
            area: entry.area ?? null,
            tooth: entry.tooth ?? null,
            surf: entry.surf ?? null,
          })),
        )}
      </div>
    )
  }

  const columnsProcedures = useMemo(() => {
    return [
      {
        field: 'space',
        headerText: '',
        width: '12px',
      },
      {
        field: 'procCode',
        headerText: 'Code',
        width: '68px',
      },
      {
        field: 'description',
        headerText: 'Description',
        width: 'auto',
      },
      {
        field: 'toothsurface',
        headerText: 'T/S',
        template: toothSurfaceTemplate,
        width: '120px',
      },
    ].map((column) => ({
      ...column,
    }))
  }, [])

  const procedureDataArray =
    formData.procedures?.map((procedure) => {
      const noTS = procedure.source === 2 && procedure.parentId === null
      const typedEntries =
        procedure.tooth_details ?? ([] as Partial<IProcedureLogs>[])

      const hasNoAreaType = typedEntries.every(
        (e) => e.area_type === null || e.area_type === undefined,
      )

      return {
        ...procedure,
        procCode: procedure.proc_code,
        description: procedure.proc_description,
        noTS,
        hasNoAreaType,
        entries: typedEntries,
        ...(!procedure.id ? { id: procedure.proc_id } : {}),
        ...(!procedure.parentId ? { parentId: null } : {}),
      }
    }) ?? []

  const updatedNotesList = (): Partial<INote>[] => {
    const tempContextId = appointmentId ?? uuid()
    return (formData.notes ?? []).map((note) => ({
      note_id: (note.note_id ?? note.note_id ?? uuid()) as string,
      title: note.title ?? '',
      body: note.body ?? (note as { content?: string }).content ?? '',
      created_by: user.user_id,
      updated_by: user.user_id,
      date_time_created_at: note.date_time_created_at
        ? note.date_time_created_at
        : moment().format('YYYY-MM-DD HH:mm:ss'),
      date_time_updated_at: note.date_time_updated_at
        ? note.date_time_updated_at
        : moment().format('YYYY-MM-DD HH:mm:ss'),
      context_type: NoteContextType.APPOINTMENT,
      context_id: tempContextId,
    }))
  }

  const handleOverlayNotesSync = (notes: Partial<INote>[]): void => {
    handleChange('notes', notes)
  }

  useEffect(() => {
    setNotesList(updatedNotesList())
  }, [formData.notes])

  return (
    <div className="appt-modal review">
      <div className="d-flex justify-content-between align-items-center pb-1 pt-2">
        <Form.Label className="review-title">
          Review the scheduling info
        </Form.Label>
        <Button
          size="sm"
          className="button-tertiary"
          onClick={handleEditButton}
          leftIcon={<i className="ti ti-pencil"></i>}
        >
          Edit
        </Button>
      </div>

      <div className="accordion" id="accordionReview">
        <div className="accordion-item" key={'scheduling'}>
          <h2 className="ac-header appt-accordion">
            <button
              className="accordion-button"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#scheduling`}
              aria-expanded="true"
              aria-controls={'scheduling'}
            >
              Scheduling
            </button>
          </h2>
          <div
            id="scheduling"
            className="accordion-collapse collapse show"
            data-bs-parent="#accordionReview"
          >
            <div className="accordion-body p-2">
              {schedDetails.map((field, index) => (
                <div className="row" key={index}>
                  <div className="col field-title mb-2">{field.title}</div>
                  <div className="col field-value text-end">
                    {displayText(field.field) || '-'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="accordion-item" key={'plan'}>
          {formData.procedures?.length === 0 || skippedSteps[1] ? (
            <h2 className="appt-accordion skipped mb-0 d-flex justify-content-between align-items-center px-2 py-2">
              <span className="">Plan</span>
              <span className="appt-accordion-skip">Skipped</span>
            </h2>
          ) : (
            <h2 className="ac-header appt-accordion">
              <button
                className="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#plan"
                aria-expanded="false"
                aria-controls="#plan"
              >
                Plan
              </button>
            </h2>
          )}
          <div
            id="plan"
            className="accordion-collapse collapse"
            data-bs-parent="#accordionReview"
          >
            <div className="accordion-body p-2">
              <div className="table-responsive">
                <div className="grid-procedures-container">
                  <DataTreeGridComponent
                    data={procedureDataArray}
                    columns={columnsProcedures}
                    idMapping="id"
                    parentIdMapping="parentId"
                    treeColumnIndex={1}
                    rowHeight={36}
                    allowPaging={true}
                    allowTextWrap={true}
                    pageSize={10}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="accordion-item" key={'add-ons'}>
          {skippedSteps[2] ? (
            <h2 className="appt-accordion skipped mb-0 d-flex justify-content-between align-items-center px-2 py-2">
              <span className="">Add-ons</span>
              <span className="appt-accordion-skip">Skipped</span>
            </h2>
          ) : (
            <h2 className="ac-header appt-accordion">
              <button
                className="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#add-ons"
                aria-expanded="false"
                aria-controls="#add-ons"
              >
                Add-ons
              </button>
            </h2>
          )}

          <div
            id="add-ons"
            className="accordion-collapse collapse"
            data-bs-parent="#accordionReview"
          >
            <div className="accordion-body p-2">
              {SHOW_APPOINTMENT_LAB_ORDERS &&
                (formData.labOrders?.length ?? 0) > 0 && (
                  <Form.Group>
                    <Form.Label>Lab orders</Form.Label>
                    {formData.labOrders?.map((lab) => (
                      <div
                        key={lab.lab_case_id}
                        className="forms-list-container lgi d-flex gap-2 mb-2 
                    rounded-3 align-items-center"
                      >
                        <img
                          src={documentIcon}
                          alt="Document icon"
                          className="fs-1 text-blue-300"
                          style={{ width: '24px', height: '24px' }}
                        />
                        <div className="d-flex gap-1 w-100 justify-content-between align-items-center">
                          <div>
                            <div className="appt-file-name">
                              {lab.instructions}
                            </div>
                            <div className="appt-file-size">
                              {moment(lab.date_time_received).format(
                                'DD/MM/YYYY',
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </Form.Group>
                )}
              {((formData.assignedFormIds?.length ?? 0) > 0 ||
                (formData.files?.length ?? 0) > 0) && (
                <Form.Group>
                  <Form.Label>Forms</Form.Label>
                  {(formData.assignedFormIds ?? []).map((formTemplateId) => {
                    const resolved = formTemplateTitleById[formTemplateId]
                    const displayName =
                      resolved ??
                      (formTitlesFetchPending ? 'Loading…' : 'Untitled form')
                    return (
                      <div
                        key={formTemplateId}
                        className="forms-list-container lgi d-flex gap-2 mb-2 
                    rounded-3 align-items-center"
                      >
                        <img
                          src={documentIcon}
                          alt="Document icon"
                          className="fs-1 text-blue-300"
                          style={{ width: '24px', height: '24px' }}
                        />
                        <div className="d-flex gap-1 w-100 justify-content-between align-items-center">
                          <div>
                            <div className="appt-file-name">{displayName}</div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  {formData.files?.map((file, index) => (
                    <div
                      key={`file-${index}`}
                      className="forms-list-container lgi d-flex gap-2 mb-2 
                    rounded-3 align-items-center"
                    >
                      <img
                        src={documentIcon}
                        alt="Document icon"
                        className="fs-1 text-blue-300"
                        style={{ width: '24px', height: '24px' }}
                      />
                      <div className="d-flex gap-1 w-100 justify-content-between align-items-center">
                        <div>
                          <div className="appt-file-name">{file.name}</div>
                          <div className="appt-file-size">{file.size}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </Form.Group>
              )}
              {(formData.notes?.length ?? 0) > 0 && (
                <Form.Group>
                  <Form.Label>Notes</Form.Label>
                  <div className="table-responsive mt-2">
                    <Table className="proc-table note-table">
                      <tbody>
                        {(formData.notes || []).map((n, i) => {
                          const titleDisplay = formatNoteTitleWithVersion(
                            {
                              title: n.title ?? '',
                              version: n.version,
                              is_latest: n.is_latest,
                              note_group_id: n.note_group_id,
                              patient_id:
                                n.patient_id ?? formData.patientId ?? '',
                              contact_id: n.contact_id ?? '',
                              context_type:
                                n.context_type ?? NoteContextType.APPOINTMENT,
                              context_id: n.context_id ?? appointmentId ?? '',
                            },
                            {
                              versionChainPool: appointmentNoteVersionPool,
                            },
                          )
                          const authorDisplay = getReviewNoteAuthorLabel(
                            n,
                            user,
                            loggedInUser,
                          )
                          const rawBody =
                            n.body ?? (n as { content?: string }).content ?? ''
                          const previewText = htmlToPlainTextPreview(rawBody)
                          const createdRaw = n.date_time_created_at
                          const createdDisplay =
                            createdRaw != null &&
                            String(createdRaw).trim() !== '' &&
                            moment(createdRaw).isValid()
                              ? moment(createdRaw).format(
                                  'MM-DD-YYYY - hh:mm A',
                                )
                              : ''
                          return (
                            <tr key={i}>
                              <td className="p-2">
                                <div className="d-flex justify-content-between align-items-start gap-2">
                                  <div className="flex-grow-1 min-w-0">
                                    <div className="d-flex justify-content-between align-items-baseline gap-3 note-table-note-topline">
                                      <div className="note-table-title-by text-truncate min-w-0">
                                        <span className="note-table-title-text">
                                          {titleDisplay}
                                        </span>
                                        <span className="note-table-by-sep">
                                          {' '}
                                          by{' '}
                                        </span>
                                        <span className="note-table-author-text">
                                          {authorDisplay}
                                        </span>
                                      </div>
                                      <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                        <span className="small text-muted note-table-created">
                                          {createdDisplay}
                                        </span>
                                      </div>
                                    </div>
                                    {previewText ? (
                                      <div className="note-table-preview">
                                        {previewText}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>
                </Form.Group>
              )}
            </div>
          </div>
        </div>
      </div>
      <Row className="mb-2 gx-2">
        <Col md="auto" className="d-flex align-items-center appt-route-info">
          Inform patient via
        </Col>
        <Col md="auto">
          <Form.Group className="">
            <div className="d-flex align-items-center">
              {['Phone', 'SMS', 'Email', 'App'].map((method) => (
                <Form.Check
                  key={method}
                  inline
                  type="checkbox"
                  label={method}
                  checked={formData.contactVia?.includes(method) || false}
                  onChange={(e) => {
                    const updatedContactVia = e.target.checked
                      ? [...(formData.contactVia ?? []), method] // Add method if checked
                      : (formData.contactVia ?? []).filter((m) => m !== method) // Remove if unchecked

                    handleChange('contactVia', updatedContactVia) // Update formData
                  }}
                />
              ))}
            </div>
          </Form.Group>
        </Col>
      </Row>
    </div>
  )
}

export default Review
